import React from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { ChronoUnit, DateTimeFormatter, Instant, ZonedDateTime, ZoneId } from "@js-joda/core";
import { TimeOptions } from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { WeatherMonitoringProps } from "../charts/interfaces";
import {
  getAverageWindDirection,
  getCurrentHourParameter,
  getMaxParameter,
  getMinMaxWindSpeed,
} from "./WeatherMonitoringUtils";

const createColumnBody = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  currentStartTime: ZonedDateTime,
  currentEndTime: ZonedDateTime,
  timeInterval: string
) => {
  const temperatureDataArr = datasetsData?.find(
    d => d.identifier === props.temperature?.dataset?.identifier
  )?.data;
  const humidityDataArr = datasetsData?.find(
    d => d.identifier === props.humidity?.dataset?.identifier
  )?.data;
  const precipitationDataArr = datasetsData?.find(
    d => d.identifier === props.precipitation?.dataset?.identifier
  )?.data;
  const windSpeedDataArr = datasetsData?.find(
    d => d.identifier === props.windSpeed?.dataset?.identifier
  )?.data;
  const windDirectionDataArr = datasetsData?.find(
    d => d.identifier === props.windDirection?.dataset?.identifier
  )?.data;

  const maxTemperature = getMaxParameter(
    temperatureDataArr || [],
    currentStartTime,
    currentEndTime,
    "Temperature"
  );
  const { min: minWindSpeed, max: maxWindSpeed } = getMinMaxWindSpeed(
    windSpeedDataArr || [],
    currentStartTime,
    currentEndTime
  );
  const avgWindDirection = getAverageWindDirection(
    windDirectionDataArr || [],
    currentStartTime,
    currentEndTime
  );

  const maxHumidity = getMaxParameter(
    humidityDataArr || [],
    currentStartTime,
    currentEndTime,
    "Humidity"
  );
  const maxPrecipitation = getMaxParameter(
    precipitationDataArr || [],
    currentStartTime,
    currentEndTime,
    "Precipitation"
  );

  return (
    <div className="flex flex-column gap-2 font-semibold">
      <div style={{ color: `${props.temperature?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxTemperature
          : getCurrentHourParameter(temperatureDataArr || [], currentStartTime)}{" "}
        °C
      </div>
      <div style={{ color: `${props.humidity?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxHumidity
          : getCurrentHourParameter(humidityDataArr || [], currentStartTime)}
        %
      </div>
      <div style={{ color: `${props.precipitation?.dataset?.line?.color}` }}>
        {timeInterval === TimeOptions.DAYS.name
          ? maxPrecipitation
          : getCurrentHourParameter(precipitationDataArr || [], currentStartTime)}{" "}
        mm
      </div>
      {timeInterval === TimeOptions.DAYS.name ? (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>
          {minWindSpeed} - {maxWindSpeed} m/s
        </div>
      ) : (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>
          {getCurrentHourParameter(windSpeedDataArr || [], currentStartTime)} m/s
        </div>
      )}

      <Button
        icon="pi pi-arrow-up"
        disabled
        className="p-button-text p-button-secondary p-0"
        style={{ transform: `rotate(${avgWindDirection}deg)` }}
      />
    </div>
  );
};

const generateColumns = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  timeInterval: string,
  startTime: Date
) => {
  const totalIntervals = timeInterval === TimeOptions.HOURS.name ? 48 : 7;
  
  let localStartTime = ZonedDateTime.ofInstant(
    Instant.ofEpochMilli(startTime.getTime()),
    ZoneId.systemDefault()
  );

  if (timeInterval === TimeOptions.HOURS.name) {
    localStartTime = localStartTime.truncatedTo(ChronoUnit.HOURS);
  } else if (timeInterval === TimeOptions.DAYS.name) {
    localStartTime = localStartTime.withHour(0).withMinute(0).withSecond(0).withNano(0);
  }

  let startZonedDateTimeUTC = localStartTime.withZoneSameInstant(ZoneId.of("UTC"));
  let startZonedDateTimeLocal = startZonedDateTimeUTC.withZoneSameInstant(ZoneId.systemDefault());

  return Array.from({ length: totalIntervals }, (_, i) => {
    const currentStartTimeUTC = startZonedDateTimeUTC;
    let currentEndTimeUTC;
    const currentStartTimeLocal = startZonedDateTimeLocal;

    if (timeInterval === TimeOptions.DAYS.name) {
      currentEndTimeUTC = currentStartTimeUTC.plusDays(1).withHour(0).withMinute(0).withSecond(0).withNano(0);

      startZonedDateTimeUTC = currentEndTimeUTC;
      startZonedDateTimeLocal = currentEndTimeUTC.withZoneSameInstant(ZoneId.systemDefault());
    } else {
      currentEndTimeUTC = currentStartTimeUTC.plusHours(1);
      startZonedDateTimeUTC = currentEndTimeUTC;
      startZonedDateTimeLocal = currentEndTimeUTC.withZoneSameInstant(ZoneId.systemDefault());
    }

    const headerFormat = timeInterval === TimeOptions.DAYS.name ? "MM-dd" : "HH:mm";
    const headerLabel = currentStartTimeLocal.format(DateTimeFormatter.ofPattern(headerFormat));

    return (
      <Column
        key={i}
        field={`weatherIcon${i}`}
        style={{ width: timeInterval === TimeOptions.HOURS.name ? "5rem" : "20rem" }}
        header={headerLabel}
        body={() =>
          createColumnBody(
            datasetsData,
            props,
            currentStartTimeUTC,
            timeInterval === TimeOptions.DAYS.name ? currentEndTimeUTC.plusDays(1).minusNanos(1) : currentEndTimeUTC,
            timeInterval
          )
        }
      />
    );
  });
};

export default generateColumns;
